'use client';

import {
  Archive,
  Bell,
  BellRing,
  Bookmark,
  Briefcase,
  Database,
  FileSearch,
  FileText,
  Folder,
  Gavel,
  GraduationCap,
  History,
  House,
  Landmark,
  LayoutDashboard,
  Layers,
  ListChecks,
  LogOut,
  Search,
  Settings,
  Sparkles,
  Table,
  Workflow,
  Zap,
} from 'lucide-react';
import React from 'react';
import {
  getLegalDivisionNavGroups,
  getRegulationProfessionalNavGroups,
  getTrialNavGroups,
} from '@/components/package-nav-groups';
import { useWorkspace } from '@/components/workspace/workspace-context';
import {
  FLAT_NAVIGATION_SCHEMA,
  FlatNavGroup,
  NavItemSchema,
} from '@/config/navigation-schema';
import { NavigationAccessUser, checkNavigationAccess } from '@/hooks/useNavigationAccess';
import { hasRegulationProfessionalRole, isLegalDivisionView, isRegulationPackageOnly } from '@/lib/access-check';

export const materialIcons = {
  search: 'search',
  fileText: 'description',
  scale: 'balance',
  bookA: 'spellcheck',
  fileCheck: 'task_alt',
  userCheck: 'how_to_reg',
  gavel: 'gavel',
  clipboardList: 'assignment',
  helpCircle: 'help',
  send: 'send',
  settings: 'settings',
  logout: 'logout',
  chevronUp: 'expand_less',
  chevronDown: 'expand_more',
  ellipsisVertical: 'more_vert',
  sparkles: 'auto_awesome',
  dashboard: 'dashboard',
  notifications: 'notifications',
  accountBalance: 'account_balance',
};

export const taskIcons: Record<string, string> = {
  'navigation-home': 'dashboard',
  'navigation-regulation': 'account_balance',
  'navigation-court-case': 'gavel',
  'navigation-workspace': 'work',
  'search-indonesia-regulation': materialIcons.search,
  'summarize-regulation': materialIcons.fileText,
  'create-legal-opinion': materialIcons.scale,
  'legal-terminology': materialIcons.bookA,
  'summarize-contract': materialIcons.fileCheck,
  'indonesia-employment-contract-review': materialIcons.userCheck,
  'summarize-court-decision': materialIcons.gavel,
  'case-briefs': materialIcons.clipboardList,
  'trademark-explorer': 'verified',
  'reg-search': 'search',
  'reg-browse': 'library_books',
  'reg-research-agent': 'auto_awesome',
  'reg-library': 'bookmark',
  'reg-history': 'history',
  'reg-notification': 'notifications',
  'tm-explorer': 'search',
  'tm-classification': 'auto_awesome',
  'cc-constitutional': 'account_balance',
  'cc-jurisprudence': 'gavel',
  'cases-files': 'folder',
  'workspace-templates': 'description',
  'workspace-checklists': 'assignment_turned_in',
  'workspace-regulation-checklist': 'assignment_turned_in',
  'tabular-reviews': 'table_chart',
  'document-review': 'fact_check',
  'workflow-builder': 'account_tree',
  'reminder': 'alarm',
  'legal-register': 'inventory_2',
  'legal-register-early-warning': 'notifications_active',
  'workspace-assistant': 'smart_toy',
  tutorial: 'school',
};

/**
 * Compute the effective href for a nav item, taking workspace context into account.
 */
export function resolveWorkspaceHref(
  item: NavItemSchema,
  workspaceContext: ReturnType<typeof useWorkspace> | null,
): string {
  const isWorkspaceTask = item.id === 'navigation-workspace';
  if (isWorkspaceTask && workspaceContext?.currentWorkspaceId) {
    return workspaceContext.currentCaseId
      ? `/id/workspace/${workspaceContext.currentWorkspaceId}/case/${workspaceContext.currentCaseId}`
      : `/id/workspace/${workspaceContext.currentWorkspaceId}`;
  }
  return item.href;
}

/**
 * Determine whether a path is active based on the item's href and relatedPaths.
 */
export function isItemActive(
  item: NavItemSchema,
  pathname: string,
): boolean {
  const inclusionPaths = item.relatedPaths?.filter((p) => !p.startsWith('!')) || [];
  const exclusionPaths = item.relatedPaths?.filter((p) => p.startsWith('!')).map((p) => p.slice(1)) || [];
  const matchesInclusion = inclusionPaths.some((path) => pathname.startsWith(path));
  const matchesExclusion = exclusionPaths.some((path) => pathname.startsWith(path));
  return pathname.startsWith(item.href) || (matchesInclusion && !matchesExclusion);
}

/**
 * Whether clicking a nav item navigates to its href instead of opening the
 * upgrade/login dialog. Items with `lockedNavigates` (e.g. the workspace
 * assistant, whose page renders its own upsell) stay clickable for logged-in
 * users who lack the tier; logged-out users still get the login dialog.
 */
export function isNavItemNavigable(
  item: NavItemSchema,
  access: { isAccessible: boolean; needsLogin: boolean },
): boolean {
  return access.isAccessible || (item.lockedNavigates === true && !access.needsLogin);
}

export function MaterialIcon({
  iconName,
  size = 20,
  className = '',
}: {
  iconName: string;
  size?: number;
  className?: string;
}) {
  return (
    <span
      className={`material-icons flex items-center justify-center ${className}`}
      style={{ fontSize: size }}
    >
      {iconName}
    </span>
  );
}

/**
 * Preview sidebar icon: inline lucide SVGs with one uniform stroke so every
 * row shares identical icon weight. Keyed by nav item id (plus the legacy
 * material names used as collapsible group icons). Falls back to the
 * filled material glyph (via taskIcons) for unknown keys.
 */
const PREVIEW_ICONS = {
  'navigation-home': House,
  'workspace-assistant': Sparkles,
  'reg-dashboard': LayoutDashboard,
  'reg-search': Search,
  'cc-putusan': Gavel,
  'ma-extension-directory': Landmark,
  'reg-browse': Database,
  'reg-consolidation': Layers,
  'reg-library': Bookmark,
  'reg-history': History,
  'reg-research-agent': Zap,
  'tabular-reviews': Table,
  'document-review': FileSearch,
  'cases-files': Briefcase,
  'workspace-templates': FileText,
  'workspace-checklists': ListChecks,
  'workspace-regulation-checklist': ListChecks,
  'workflow-builder': Workflow,
  reminder: Bell,
  'legal-register': Archive,
  'legal-register-early-warning': BellRing,
  'workspace-dashboard': LayoutDashboard,
  'document-tool': FileText,
  tutorial: GraduationCap,
  settings: Settings,
  logout: LogOut,
  // Collapsible group header aliases (legacy material names).
  storage: Database,
  auto_awesome: Sparkles,
  folder: Folder,
} as const;

export function PreviewNavIcon({
  iconKey,
  size = 20,
  className = '',
}: {
  iconKey: string;
  size?: number;
  className?: string;
}) {
  const Icon = (PREVIEW_ICONS as Record<string, typeof House>)[iconKey];
  if (!Icon) {
    // Nav item ids are not glyph names: resolve them through taskIcons so an
    // unmapped item never renders its id as text (which squeezes the label).
    const glyph = taskIcons[iconKey] ?? (/^[a-z0-9_]+$/.test(iconKey) ? iconKey : 'circle');
    return <MaterialIcon iconName={glyph} size={size} className={className} />;
  }
  return <Icon size={size} strokeWidth={2} className={className} aria-hidden="true" />;
}

interface AccessibleGroupsOptions {
  user: NavigationAccessUser | null | undefined;
  /** True for companies that opted out of regulation notifications. */
  hideRegulationNotification?: boolean;
}

/** Role id of the free-trial compliance package (turunan Elite). */
export const COMPLIANCE_TRIAL_ROLE = 'compliance_trial';

/**
 * Whether the user gets the scoped trial sidebar instead of the full tree.
 * Resolved from raw session roles; access itself still flows through the
 * elite flags the role inherits, so no gate changes are needed elsewhere.
 * Admins always keep the full view.
 */
export function isComplianceTrialUser(user: NavigationAccessUser | null | undefined): boolean {
  if (!user || user.isAdmin) return false;
  return user.roles?.includes(COMPLIANCE_TRIAL_ROLE) ?? false;
}

/**
 * Whether the sidebar shows the notification bell. Elite features
 * (workspace sharing invitations) and the regulation package (new-regulation
 * alerts) are the producers of bell entries; other tiers would only ever
 * see an empty bell.
 */
export function canSeeNotificationBell(user: NavigationAccessUser | null | undefined): boolean {
  if (!user) return false;
  return Boolean(user.isAdmin || user.hasEliteAccess || hasRegulationProfessionalRole(user.roles ?? []));
}

/**
 * Builds the sidebar groups from the single flat schema, then filters items
 * by tier and alpha flags. Shared by the desktop sidebar and the mobile
 * menu sheet so both stay in sync.
 */
export function getAccessibleNavGroups({
  user,
  hideRegulationNotification = false,
}: AccessibleGroupsOptions): FlatNavGroup[] {
  // The legal-division package outranks the compliance trial: it is the paid,
  // broader entitlement, and /id/home already lands its holders on the dashboard.
  if (user && isLegalDivisionView(user)) return getLegalDivisionNavGroups();
  if (isComplianceTrialUser(user)) return getTrialNavGroups();
  if (user && isRegulationPackageOnly(user)) return getRegulationProfessionalNavGroups();
  return FLAT_NAVIGATION_SCHEMA.map((group) => ({ 
    ...group,
    items: group.items.filter((item) => {
      // Explicitly hidden items stay hidden for everyone, admins included.
      if (item.hidden) return false;

      // Companies without notifications never see the notification page.
      if (hideRegulationNotification && item.id === 'reg-notification') return false;

      // Admin users always see all navigation items without restriction.
      if (user?.isAdmin) return true;

      // Restricted items vanish for roles that lack the feature, instead of
      // showing a lock that invites an upgrade they cannot buy.
      if (
        item.hideWithoutFeature &&
        item.featureId &&
        !user?.allowedFeatures?.includes(item.featureId)
      ) {
        return false;
      }

      const access = checkNavigationAccess(item, user);
      if (access.isAlphaFeature && !user?.isAdmin) return false;

      // Tier/login-locked items stay visible but unopenable: renderers
      // link them to '#' and open the upgrade/login dialog on click.
      return true;
    }),
  })).filter((group) => group.items.length > 0);
}
